import {
  type DeviceGroupAttribute,
  type GraphPath,
  IPF_NETWORK_VIEWER,
  IPF_NO_VALUE_CLOUD,
  type PathLookupType,
} from '@ipfabric/utils';
import { getPathNameLabel } from 'components/Graph/helpers/getPathNameLabel';
import type { GraphPathWarnings } from 'components/Graph/model/selectors/graphMetadata/selectPathWarnings';

export const BREADCRUMB_PATHLOOKUP_DEFAULT_GW = 'default-gateway';

export interface NetworkViewerModeBreadcrumb {
  type: 'networkViewer';
  groupBy: DeviceGroupAttribute;
}

export interface PathLookupModeBreadcrumb {
  type: 'pathLookup';
  pathLookupType: PathLookupType;
}

export interface NetworkPathBreadcrumb {
  type: 'path';
  empty: boolean;
  hidden: boolean;
  missing: boolean;
  path: GraphPath;
  label?: string;
}

export interface PathLookupInstanceBreadcrumb {
  type: 'pathLookupInstance';
  source: string;
  sourcePort: string | null;
  destination: string | typeof BREADCRUMB_PATHLOOKUP_DEFAULT_GW;
  destinationPort: string | null;
}

export interface LoadedViewBreadcrumb {
  type: 'loadedView';
  label: string;
}

export interface CompareModeBreadcrumb {
  type: 'compareMode';
  snapshotName: string;
  snapshotToCompareName: string;
}

export interface IntentCheckBreadcrumb {
  type: 'intentCheck';
  ruleName: string;
}

export type GraphBreadcrumb =
  | NetworkViewerModeBreadcrumb
  | PathLookupModeBreadcrumb
  | NetworkPathBreadcrumb
  | LoadedViewBreadcrumb
  | PathLookupInstanceBreadcrumb
  | CompareModeBreadcrumb
  | IntentCheckBreadcrumb;

export type GraphBreadcrumbType = GraphBreadcrumb['type'];

interface BreadcrumbsInput {
  compare: null | {
    snapshotName: string;
    snapshotToCompareName: string;
  };
  groupBy: DeviceGroupAttribute | null;
  intentRuleName: string | null;
  pathLookup: null | {
    source: string;
    sourcePort: string | null;
    destination: string | typeof BREADCRUMB_PATHLOOKUP_DEFAULT_GW;
    destinationPort: string | null;
    pathLookupType: PathLookupType;
  };
  paths: GraphPath[];
  pathWarnings: GraphPathWarnings;
  loadedViewName: string | null;
}

const buildPathLookupBreadcrumbs = (
  input: BreadcrumbsInput,
): GraphBreadcrumb[] => [
  {
    type: 'pathLookup',
    pathLookupType: input.pathLookup!.pathLookupType,
  },
  {
    type: 'pathLookupInstance',
    ...input.pathLookup!,
  },
];

const buildNetworkViewerBreadcrumbs = (
  input: BreadcrumbsInput,
): GraphBreadcrumb[] => {
  if (input.groupBy === null) {
    return [];
  }

  const result: GraphBreadcrumb[] = [
    {
      type: 'networkViewer',
      groupBy: input.groupBy,
    },
  ];

  const sortedPaths = [...input.paths]
    // First 'All networks', then 'No Value', then other sites
    .sort((a, b) => {
      if (a === IPF_NETWORK_VIEWER) {
        return -1;
      }
      if (b === IPF_NETWORK_VIEWER) {
        return 1;
      }
      if (a === IPF_NO_VALUE_CLOUD) {
        return -1;
      }
      if (b === IPF_NO_VALUE_CLOUD) {
        return 1;
      }
      return a.localeCompare(b);
    });
  for (const path of sortedPaths) {
    result.push({
      type: 'path',
      empty: input.pathWarnings.emptyPaths.has(path),
      hidden: input.pathWarnings.hiddenPaths.has(path),
      missing: input.pathWarnings.missingPaths.has(path),
      path,
      label: getPathNameLabel(path),
    });
  }
  return result;
};

const buildCompareModeBreadcrumbs = (
  input: BreadcrumbsInput,
): GraphBreadcrumb[] =>
  input.compare
    ? [
        {
          type: 'compareMode',
          snapshotName: input.compare.snapshotName,
          snapshotToCompareName: input.compare.snapshotToCompareName,
        },
      ]
    : [];

const buildIntentCheckBreadcrumbs = (
  input: BreadcrumbsInput,
): GraphBreadcrumb[] =>
  input.intentRuleName
    ? [
        {
          type: 'intentCheck',
          ruleName: input.intentRuleName,
        },
      ]
    : [];

const buildLoadedViewBreadcrumbs = (
  input: BreadcrumbsInput,
): GraphBreadcrumb[] =>
  input.loadedViewName !== null
    ? [
        {
          type: 'loadedView',
          label: input.loadedViewName || '(unnamed view)',
        },
      ]
    : [];

export const buildBreadcrumbs = (
  input: BreadcrumbsInput,
): GraphBreadcrumb[] => [
  ...(input.pathLookup
    ? buildPathLookupBreadcrumbs(input)
    : buildNetworkViewerBreadcrumbs(input)),
  ...buildCompareModeBreadcrumbs(input),
  ...buildIntentCheckBreadcrumbs(input),
  ...buildLoadedViewBreadcrumbs(input),
];
